// AIRQU-37：「上架產品」入口改為 /admin/products?action=new，配合 ProductManagement.js
// 讀取 query 自動彈出新增產品 Modal（方案 B）。主要實作 commit：7e7217c，
// 已於 2026-07-04 推送至 origin/main，PM 廖書鈺同日回訊「請確認有push 成功」——確認推送無誤。
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTemplates } from '../../context/TemplatesContext';
import { useAuth } from '../../context/AuthContext';
import './Header.css';
import { FaHome, FaFileAlt, FaUsers, FaCaretDown, FaUserCircle, FaSignOutAlt, FaBars, FaTimes, FaCog, FaBoxOpen } from 'react-icons/fa';

const Header = () => {
  const { templates } = useTemplates();
  const { currentUser, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navDropdownRefs = useRef({});

  // Use company name from templates (unified source)
  const companyName = templates?.companyName || currentUser?.companyName || '公司';
  
  // Check different user roles
  const isSuperAdmin = currentUser && currentUser.role === 'superadmin';
  const isAdmin = currentUser && currentUser.role === 'admin';

  // Check permissions for different menu items
  const canManageUsers = isSuperAdmin || isAdmin; // Only superadmin and full admin can manage users
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close mobile menu when clicking outside or on ESC key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const hamburger = document.querySelector('.hamburger-menu');
        if (hamburger && !hamburger.contains(event.target)) {
          setIsMobileMenuOpen(false);
          setOpenNavDropdown(null);
        }
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setOpenNavDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Prevent body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close nav dropdowns when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openNavDropdown && window.innerWidth > 1024) {
        const isClickInsideDropdown = Object.values(navDropdownRefs.current).some(ref =>
          ref && ref.contains(event.target)
        );
        if (!isClickInsideDropdown) {
          setOpenNavDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavDropdown]);
  
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenNavDropdown(null);
  };

  const toggleNavDropdown = (menuName) => {
    setOpenNavDropdown(openNavDropdown === menuName ? null : menuName);
  };
  
  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {/* 漢堡選單按鈕 (僅在手機版顯示) */}
          <button className="hamburger-menu" onClick={toggleMobileMenu} aria-label="選單">
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          
          {/* 遮罩層 */}
          {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}
          
          <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} ref={mobileMenuRef}>
            <ul>
              <li>
                <Link to="/admin" onClick={closeMobileMenu}><FaHome /> 儀表板</Link>
              </li>

              {/* 報價管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['quotation'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('quotation')}
                >
                  <span><FaFileAlt /> 報價管理</span>
                  <FaCaretDown className={openNavDropdown === 'quotation' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'quotation' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <Link to="/admin/quotations" onClick={closeMobileMenu}>報價單</Link>
                    </li>
                    <li>
                      <Link to="/admin/estimations" onClick={closeMobileMenu}>詢價單</Link>
                    </li>
                  </ul>
                )}
              </li>

              {/* 產品管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['product'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('product')}
                >
                  <span><FaBoxOpen /> 產品管理</span>
                  <FaCaretDown className={openNavDropdown === 'product' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'product' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <Link to="/admin/products" onClick={closeMobileMenu}>產品列表</Link>
                    </li>
                    <li>
                      <Link to="/admin/products?action=new" onClick={closeMobileMenu}>上架產品</Link>
                    </li>
                  </ul>
                )}
              </li>

              {/* 系統管理下拉選單（admin/superadmin 限定） */}
              {canManageUsers && (
                <li className="nav-dropdown" ref={el => navDropdownRefs.current['sysadmin'] = el}>
                  <div
                    className="nav-dropdown-toggle"
                    onClick={() => toggleNavDropdown('sysadmin')}
                  >
                    <span><FaCog /> 系統管理</span>
                    <FaCaretDown className={openNavDropdown === 'sysadmin' ? 'rotate' : ''} />
                  </div>
                  {openNavDropdown === 'sysadmin' && (
                    <ul className="nav-dropdown-menu">
                      <li>
                        <Link to="/admin/case-studies" onClick={closeMobileMenu}>案例管理</Link>
                      </li>
                      <li>
                        <Link to="/admin/security" onClick={closeMobileMenu}>安全設定</Link>
                      </li>
                    </ul>
                  )}
                </li>
              )}

              {currentUser && (
                <li className="user-menu" ref={dropdownRef}>
                  <div className="user-menu-toggle" onClick={toggleDropdown}>
                    <span>{companyName} - {currentUser.name}</span>
                    <FaCaretDown className={isDropdownOpen ? 'rotate' : ''} />
                  </div>
                  {isDropdownOpen && (
                    <ul className="dropdown-menu">
                      <li>
                        <Link to="/profile" onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}>
                          <FaUserCircle /> 個人資料
                        </Link>
                      </li>

                      {/* 只有超級管理員和一般管理員可以管理用戶 */}
                      {canManageUsers && (
                        <li>
                          <Link
                            to={isSuperAdmin ? "/users" : `/companies/${currentUser.company?._id || currentUser.company}/users`}
                            onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}
                          >
                            <FaUsers /> 用戶管理
                          </Link>
                        </li>
                      )}
                      <li>
                        <button onClick={handleLogout}>
                          <FaSignOutAlt /> 登出
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
